import { useLazyQuery } from '@apollo/client';
import {
  Callout,
  DirectionalHint,
  ITextFieldStyles,
  Label,
  Spinner,
  Stack,
  Text,
  TextField,
  useTheme,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { useCommonStyles } from 'common/styles';
import { RequiredNameProps } from 'common/types/utility';
import { debounce } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { TripCreateFormValues } from 'travelAuthorization/TravelPlan/view/FormView/Trips/TripModal/types';
import {
  LookupSectionNameSearch,
  LookupSectionNameSearchVariables,
  LookupSectionNameSearch_lookupSectionNameSearch_nodes,
} from './__generated__/LookupSectionNameSearch';
import { useStyles } from './index.styles';
import LOOKUP_NAME from './LookupSectionNameSearch.graphql';
import { buildArrivalName } from '../../../../SectionToggle/utils';

interface ProviderSearchProps {
  tripSectionId: string | null | undefined;
  placeholder: string;
  tripIndex: number;
  label: string;
  disabled: boolean;
}

const useTextFieldStyles = (disabled: boolean) => {
  const theme = useTheme();
  const textfieldStyles = useMemo(() => {
    const styles: Partial<ITextFieldStyles> = {
      field: { height: 50 },
      fieldGroup: { height: 50 },
      suffix: {
        backgroundColor: disabled
          ? theme.palette.neutralLighter
          : theme.palette.white,
      },
    };
    return styles;
  }, [disabled, theme]);

  return textfieldStyles;
};

export const ProviderSearch: React.FC<
  RequiredNameProps<ProviderSearchProps>
> = ({ tripSectionId, name, placeholder, label, tripIndex, disabled }) => {
  const { field } = useController({ name });
  const { setValue, getValues } = useFormContext<TripCreateFormValues>();
  const styles = useStyles();
  const commonStyles = useCommonStyles();
  const textFieldStyles = useTextFieldStyles(false);
  const calloutId = useId('providerSearchCallout');
  const [callOutVisible, setCallOutVisible] = useState<boolean>(false);
  const [getLookUpdata, { data, loading }] = useLazyQuery<
    LookupSectionNameSearch,
    LookupSectionNameSearchVariables
  >(LOOKUP_NAME);

  const callbackLookUp = useCallback(
    debounce((value, queryCalled) => {
      getLookUpdata({
        variables: {
          travelSectionId: tripSectionId!,
          lookupName: value!,
          orderBy: ['LOOKUP_NAME_ASC'],
        },
      });
      queryCalled();
    }, 350),
    [field.value]
  );

  const onSearch = (_: any, value?: string) => {
    if (value) {
      field.onChange(value);
      callbackLookUp(value, () => {
        setCallOutVisible(true);
      });
    } else {
      field.onChange(null);
      setCallOutVisible(false);
    }
  };

  const renderSpinner = () => {
    return <>{loading ? <Spinner /> : null}</>;
  };

  const onSelect = (
    item: LookupSectionNameSearch_lookupSectionNameSearch_nodes
  ) => {
    field.onChange(item.lookupName);
    if (item.countryId) {
      setValue(`tripItems.${tripIndex}.rentalCountryId`, item.countryId);
    }
    if (item.stateRegionId) {
      setValue(
        `tripItems.${tripIndex}.rentalStateRegionId`,
        item.stateRegionId
      );
    }
    if (item.zipCode) {
      setValue(`tripItems.${tripIndex}.rentalPostalCode`, item.zipCode);
    }
    if (item.city) {
      setValue(`tripItems.${tripIndex}.rentalCityName`, item.city);
    }
    if (item.address1) {
      setValue(`tripItems.${tripIndex}.rentalAddress1`, item.address1);
    }
    if (item.address2) {
      setValue(`tripItems.${tripIndex}.rentalAddress2`, item.address2);
    }
    if (item.phone) {
      setValue(`tripItems.${tripIndex}.rentalTelephoneNumber`, item.phone);
    }

    // sync ground transportation (only when this row is the accommodations)
    const items = getValues('tripItems') || [];
    if (items[tripIndex]?.layout === 'TRAVEL_BOOKING_HOTEL') {
      syncGroundTransport(
        item.lookupName,
        item.address1 || items[tripIndex]?.rentalAddress1,
        item.city || items[tripIndex]?.rentalCityName
      );
    }
    setCallOutVisible(false);
  };

  const syncGroundTransport = (
    hotelName?: string | null,
    address?: string | null,
    city?: string | null
  ) => {
    const arrivalName = buildArrivalName(hotelName, address, city);
    if (!arrivalName) return;
    (getValues('tripItems') || []).forEach((row, idx) => {
      if (row.layout !== 'TRAVEL_BOOKING_GROUND_TRANS') return;
      (['pickupArrivalDropName', 'returnName'] as const).forEach((f) => {
        if (!row[f]) {
          setValue(`tripItems.${idx}.${f}` as any, arrivalName, {
            shouldDirty: true,
          });
        }
      });
    });
  };

  const syncFromCurrentHotel = () => {
    const row = (getValues('tripItems') || [])[tripIndex];
    if (row?.layout !== 'TRAVEL_BOOKING_HOTEL') return;
    syncGroundTransport(field.value, row.rentalAddress1, row.rentalCityName);
  };
  const hasResults = (data?.lookupSectionNameSearch?.nodes?.length ?? 0) > 0;
  const listOpen = callOutVisible && hasResults;
  return (
    <Stack>
      <Label className={commonStyles.colorThemePrimary}>{label}</Label>
      <TextField
        id={calloutId}
        styles={textFieldStyles}
        placeholder={placeholder}
        onChange={onSearch}
        onBlur={() => {
          // skip only when the list is really open (user may be picking from it)
          if (!listOpen) syncFromCurrentHotel();
        }}
        value={field.value || ''}
        onRenderSuffix={renderSpinner}
        disabled={disabled}
      />
      {callOutVisible && data?.lookupSectionNameSearch?.nodes?.length! > 0 && (
        <Callout
          gapSpace={10}
          onDismiss={() => {
            setCallOutVisible(false);
            syncFromCurrentHotel(); // dismissed without selecting: treat as manual entry
          }}
          isBeakVisible={false}
          className={styles.callout}
          directionalHint={DirectionalHint.bottomLeftEdge}
          target={`#${calloutId}`}
          calloutWidth={500}
        >
          <Stack className={styles.itemContainer}>
            {data?.lookupSectionNameSearch?.nodes.map((item, index) => {
              return (
                <Text
                  onClick={() => onSelect(item)}
                  className={styles.resultItem}
                  key={index.toString()}
                >
                  <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Text>{item.lookupDescription}</Text>
                  </Stack>
                </Text>
              );
            })}
          </Stack>
        </Callout>
      )}
    </Stack>
  );
};
